import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import LastPageIcon from '@mui/icons-material/LastPage'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { BidQuestion, QUESTION_PAGE_SIZE } from '../../comps/BidQuestion'
import BidQuestionsLeftPanel from './BidQuestionsLeftPanel'
import TabHeader from '../../comps/TabHeader'
import { FS_MICRO, FS_SM, ICON_LG, ICON_SM } from '../../common/theme'
import { questionAnchorId, type Project, type Question } from '../../types'

// Compact paginator used in the section-tab end slot (or standalone when there
// are no sections). Mirrors the controls in QuestionPaginator's summary block
// without the question pills.
function CompactPaginator({ page, totalPages, total, onPage }: {
  page: number; totalPages: number; total: number; onPage: (p: number) => void
}) {
  if (total === 0) return null
  const start = (page - 1) * QUESTION_PAGE_SIZE + 1
  const end = Math.min(page * QUESTION_PAGE_SIZE, total)
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Typography variant="caption" sx={{ fontSize: FS_MICRO, opacity: 0.6, whiteSpace: 'nowrap', mr: 0.5 }}>
        {start}–{end} of {total}
      </Typography>
      <IconButton size="small" disabled={page <= 1} onClick={() => onPage(1)} sx={{ p: 0.25 }}>
        <FirstPageIcon sx={{ fontSize: ICON_SM }} />
      </IconButton>
      <IconButton size="small" disabled={page <= 1} onClick={() => onPage(page - 1)} sx={{ p: 0.25 }}>
        <ChevronLeftIcon sx={{ fontSize: ICON_SM }} />
      </IconButton>
      <Typography variant="caption" sx={{ fontSize: FS_SM, mx: 0.25, whiteSpace: 'nowrap' }}>
        {page}/{totalPages}
      </Typography>
      <IconButton size="small" disabled={page >= totalPages} onClick={() => onPage(page + 1)} sx={{ p: 0.25 }}>
        <ChevronRightIcon sx={{ fontSize: ICON_SM }} />
      </IconButton>
      <IconButton size="small" disabled={page >= totalPages} onClick={() => onPage(totalPages)} sx={{ p: 0.25 }}>
        <LastPageIcon sx={{ fontSize: ICON_SM }} />
      </IconButton>
    </Box>
  )
}

interface BidQuestionsTabProps {
  project: Project
  allFilteredQuestions: Question[]
  pagedQuestions: Question[]
  totalQuestions: number
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  stateFilter?: string
  sectionFilter?: string
  onSectionChange?: (s: string | undefined) => void
  onGenerate?: (q: Question) => void
  onQuestionChange?: (q: Question) => void
  // Forwarded to the LHS panel - opens the project-level batch process modal
  // with a specific list of questions (used by the bulk-edit modal).
  onBatchProcess?: (questions: Question[]) => void
}

const ALL_KEY = '__all__'
const UNSECTIONED_KEY = '__unsectioned__'

export default function BidQuestionsTab({
  project, allFilteredQuestions, pagedQuestions, totalQuestions, currentPage, totalPages, onPageChange,
  stateFilter, sectionFilter, onSectionChange, onGenerate, onQuestionChange, onBatchProcess,
}: BidQuestionsTabProps) {

  console.log('BidQuestionsTab render');
  const { sectionList, hasUnsectioned } = useMemo(() => {
    const seen = new Set<string>()
    const list: string[] = []
    let unsectioned = false
    for (const q of project.questions ?? []) {
      const sec = (q as any).section as string | null | undefined
      if (sec) {
        if (!seen.has(sec)) { seen.add(sec); list.push(sec) }
      } else {
        unsectioned = true
      }
    }
    return { sectionList: list, hasUnsectioned: unsectioned }
  }, [project.questions])

  const showSectionTabs = sectionList.length > 0

  const activeKey =
    sectionFilter === undefined ? ALL_KEY :
      sectionFilter === '' ? UNSECTIONED_KEY :
        sectionFilter

  const sectionTabs = [
    { key: ALL_KEY, label: 'All' },
    ...sectionList.map(s => ({ key: s, label: s })),
    ...(hasUnsectioned ? [{ key: UNSECTIONED_KEY, label: 'Unsectioned' }] : []),
  ]

  const handleSectionTabChange = (key: string) => {
    if (!onSectionChange) return
    if (key === ALL_KEY) onSectionChange(undefined)
    else if (key === UNSECTIONED_KEY) onSectionChange('')
    else onSectionChange(key)
  }

  // ── Left panel state ──────────────────────────────────────────────────────
  const [leftPanelWidth, setLeftPanelWidth] = useState(280)
  const [leftCollapsed, setLeftCollapsed] = useState(false)
  const [selectedShas, setSelectedShas] = useState<Set<string>>(new Set())
  const containerRef = useRef<HTMLDivElement>(null)
  const isResizingRef = useRef(false)
  const startXRef = useRef(0)
  const startWidthRef = useRef(280)

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return
      const delta = e.clientX - startXRef.current
      const maxW = (containerRef.current?.clientWidth ?? window.innerWidth) - 200
      setLeftPanelWidth(Math.max(180, Math.min(maxW, startWidthRef.current + delta)))
    }
    const onUp = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [])

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    isResizingRef.current = true
    startXRef.current = e.clientX
    startWidthRef.current = leftPanelWidth
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  // Scroll the first question of the new page into view when the user pages
  // through the list. Skip the initial mount so the view doesn't jump on
  // first render. Defer to next frame so the new rows have actually rendered
  // (and have anchor ids) before we try to find them.
  const isInitialPageRef = useRef(true)
  useEffect(() => {
    if (isInitialPageRef.current) {
      isInitialPageRef.current = false
      return
    }
    const firstSha = (pagedQuestions[0] as any)?.questionsha
    if (!firstSha) return
    const raf = requestAnimationFrame(() => {
      document.getElementById(questionAnchorId(firstSha))?.scrollIntoView({ behavior: 'auto', block: 'start' })
    })
    return () => cancelAnimationFrame(raf)
  }, [currentPage]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box
      data-testid="bq_root"
      ref={containerRef}
      sx={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}
    >

      {/* Left panel - question navigator */}
      {!leftCollapsed && (
        <Box sx={{
          flex: `0 0 ${leftPanelWidth}px`, minWidth: 0,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <BidQuestionsLeftPanel
            allQuestions={allFilteredQuestions}
            page={currentPage}
            onPage={onPageChange}
            onCollapse={() => setLeftCollapsed(true)}
            selectedShas={selectedShas}
            onSelectionChange={setSelectedShas}
            onQuestionChange={onQuestionChange}
            onBatchProcess={onBatchProcess}
            sectionFilter={sectionFilter}
            onSectionFilterChange={onSectionChange}
          />
        </Box>
      )}

      {/* Resize handle / re-expand strip */}
      {leftCollapsed ? (
        <Box
          sx={{
            width: 18, flexShrink: 0,
            borderRight: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            pt: 0.5, bgcolor: 'action.hover',
          }}
        >
          <Tooltip title="Show questions panel" placement="right">
            <IconButton
              data-testid="bq_leftpanel_expand_btn"
              size="small"
              onClick={() => setLeftCollapsed(false)}
              sx={{ p: 0.25 }}
            >
              <ChevronRightIcon sx={{ fontSize: ICON_SM }} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Box
          onMouseDown={handleResizeStart}
          sx={{
            width: 7, flexShrink: 0,
            cursor: 'col-resize',
            borderLeft: 1, borderRight: 1, borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <MoreVertIcon sx={{ fontSize: ICON_LG, opacity: 0.35 }} />
        </Box>
      )}

      {/* Right side - section filter + paginator + question list */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {showSectionTabs ? (
          <TabHeader
            tabs={sectionTabs}
            active={activeKey}
            onChange={handleSectionTabChange}
            endSlot={
              <CompactPaginator
                page={currentPage}
                totalPages={totalPages}
                total={totalQuestions}
                onPage={onPageChange}
              />
            }
            testIdPrefix="bidsection"
          />
        ) : (
          /* No sections - still show the paginator as a standalone toolbar. */
          totalQuestions > 0 && (
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              px: 1, py: 0.5,
              borderBottom: 1, borderColor: 'divider',
              flexShrink: 0,
            }}>
              <CompactPaginator
                page={currentPage}
                totalPages={totalPages}
                total={totalQuestions}
                onPage={onPageChange}
              />
            </Box>
          )
        )}

        <Box data-testid="bq_list" sx={{ flex: 1, overflowY: 'auto' }}>
          {pagedQuestions.length === 0 ? (
            <Box data-testid="bq_empty" sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.45 }}>
                {stateFilter ? 'No questions match this filter.' : 'No questions found.'}
              </Typography>
            </Box>
          ) : (
            pagedQuestions.map((q, i) => (
              <BidQuestion
                key={q.questionsha ?? i}
                question={q}
                projectsha={project.projectsha}
                paginStartIndex={(currentPage - 1) * QUESTION_PAGE_SIZE + i}
                onGenerate={onGenerate}
              // onQuestionChange={onQuestionChange}
              />
            ))
          )}
        </Box>
      </Box>
    </Box>
  )
}
